import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface TranslationsContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.features': 'Features',
    'nav.contact': 'Contact',
    'nav.companies': 'Companies',
    'nav.signin': 'Sign In',
    'nav.bookDemo': 'Get Started',
    
    // Hero Section
    'hero.badge': 'Middle East ESG Intelligence Platform',
    'hero.title1': 'ESG Intelligence for',
    'hero.title2': ' Investors',
    'hero.description': 'Access comprehensive ESG ratings for 880+ listed companies across the Middle East. Build portfolios, track performance, and make informed investment decisions.',
    'hero.startTrial': 'Sign in',
    'hero.tryFree': 'Start Trial',
    'hero.watchDemo': 'Watch Demo',
    'hero.esgScore': 'Average ESG Score',
    'hero.excellent': 'Excellent',
    'hero.environmental': 'Environmental',
    'hero.social': 'Social',
    'hero.governance': 'Governance',
    
    // Stats
    'stats.companies': '880+',
    'stats.companiesLabel': 'Listed Companies Covered',
    'topFive.rankings': 'ESG Rankings',
    'topFive.performer': 'Top ESG Performer',
    'topFive.scoreOutOf': 'Score out of 100',
    'stats.countries': '10+',
    'stats.countriesLabel': 'Middle East Markets',
    'stats.kpis': '150+',
    'stats.kpisLabel': 'ESG Indicators Tracked',
    'stats.years': '10+',
    'stats.yearsLabel': 'Years of ESG Data',
    
    // Features Section
    'features.title': 'ESG Ratings, Data, and Insights for Investors',
    'features.subtitle': 'Comprehensive tools and data designed to help investors evaluate sustainability performance across the Middle East region.',
    'features.companyRatings.title': 'Company ESG Ratings',
    'features.companyRatings.desc': 'Access detailed ESG ratings for 880+ listed companies across Middle East stock exchanges with our proprietary methodology.',
    'features.portfolioBuilder.title': 'Portfolio Builder',
    'features.portfolioBuilder.desc': 'Create and manage investment portfolios while tracking their aggregate ESG performance over time.',
    'features.benchmarking.title': 'Company Benchmarking',
    'features.benchmarking.desc': 'Compare ESG performance across companies, sectors, and markets to identify leaders and laggards.',
    'features.kpiAccess.title': 'Detailed ESG KPIs',
    'features.kpiAccess.desc': 'Dive deep into 150+ individual ESG metrics covering environmental impact, social responsibility, and governance practices.',
    'features.analytics.title': 'Historical Analytics',
    'features.analytics.desc': 'Track ESG rating trends over multiple years to understand company trajectories and improvement patterns.',
    'features.reports.title': 'Premium Reports',
    'features.reports.desc': 'Purchase comprehensive ESG analytics reports with detailed insights, recommendations, and peer comparisons.',
    
    // Benefits Section
    'benefits.title': 'Make Informed Sustainable Investments',
    'benefits.subtitle': 'Leverage ESG ratings, sustainability indicators, and analytical tools to support responsible investment strategies across Middle East capital markets.',
    'benefits.item1': 'Access ESG ratings for 880+ Middle East listed companies',
    'benefits.item2': 'Track portfolio ESG performance over time',
    'benefits.item3': 'Benchmark companies against industry peers',
    'benefits.item4': 'Analyze 150+ detailed ESG indicators',
    'benefits.item5': 'Download comprehensive ESG analytics reports',
    'benefits.highlights': 'Platform Highlights',
    'benefits.coverage': 'Regional Coverage',
    'benefits.coverageDesc': 'Coverage across major Middle East stock exchanges, providing ESG insights for companies listed across the region.',
    'benefits.methodology': 'Trusted Methodology',
    'benefits.methodologyDesc': 'ESG ratings are based on the ESG Invest® proprietary rating framework, developed using internationally recognized ESG standards and regional expertise.',
    'benefits.updates': 'Regular Updates',
    'benefits.updatesDesc': 'ESG ratings and underlying data are updated regularly to reflect the latest corporate disclosures and sustainability performance developments.',
    'benefits.support': 'Expert Support',
    'benefits.supportDesc': 'Dedicated ESG analysts provide guidance and support to investors integrating ESG considerations into investment analysis, portfolio construction, and responsible investment strategies.',
    
    // CTA Section
    'cta.title': 'Start exploring ESG insights across Middle East markets',
    'cta.subtitle': 'Access ESG ratings, benchmarking tools, and sustainability insights covering 880+ listed companies across Middle East markets.',
    'cta.scheduleDemo': 'Start Free Trial',
    'cta.startForFree': 'Explore the Platform',
    
    // Features Page
    'featuresPage.badge': 'Platform Capabilities',
    'featuresPage.title': 'Powerful ESG investment tools',
    'featuresPage.subtitle': 'Discover how ESG Invest helps you evaluate and track sustainable investments across the Middle East.',
    'featuresPage.keyFeatures': 'Key Features',
    'featuresPage.feature1.title': 'Company ESG Ratings',
    'featuresPage.feature1.desc': 'Access comprehensive ESG ratings for 880+ listed companies across more than 10 Middle East markets, based on the ESG Invest® proprietary methodology.',
    'featuresPage.feature2.title': 'Company Benchmarking',
    'featuresPage.feature2.desc': 'Compare ESG performance across companies, sectors, and countries to identify sustainability leaders, laggards, and investment opportunities.',
    'featuresPage.feature3.title': 'Portfolio Management',
    'featuresPage.feature3.desc': 'Build ESG-aware investment portfolios and track their aggregated ESG ratings and sustainability performance over time.',
    'featuresPage.feature4.title': 'Premium Analytics Reports',
    'featuresPage.feature4.desc': 'Purchase detailed ESG company reports with deep-dive analysis, peer comparisons, and actionable insights.',
    'featuresPage.feature5.title': 'Trend Tracking',
    'featuresPage.feature5.desc': 'Analyze ESG rating trends over multiple years to identify improving and declining performers.',
    'featuresPage.feature6.title': 'Detailed ESG KPIs',
    'featuresPage.feature6.desc': 'Access 150+ individual metrics across environmental, social, and governance categories.',
    'featuresPage.feature7.title': 'Sector Analysis',
    'featuresPage.feature7.desc': 'Evaluate ESG performance by industry sector to find sustainable leaders in each market segment.',
    'featuresPage.feature8.title': 'Country Coverage',
    'featuresPage.feature8.desc': 'Complete coverage of major Middle East stock exchanges including UAE, Saudi Arabia, Kuwait, and more.',
    'featuresPage.feature9.title': 'Alert Notifications',
    'featuresPage.feature9.desc': 'Get notified when companies in your portfolio have ESG rating changes or significant updates.',
    'featuresPage.feature10.title': 'Data Export',
    'featuresPage.feature10.desc': 'Export ESG ratings and underlying data for integration with investment research tools and portfolio analytics systems.',
    
    // ESG Integration Tools Section
    'integration.title': 'ESG Integration Tools',
    'integration.desc1': 'Support the integration of environmental, social, and governance considerations into investment strategies and financial products.',
    'integration.desc2': 'The platform provides guidance and practical tools to help investors and market participants incorporate ESG factors into investment processes and decision-making.',
    'integration.capabilities': 'Capabilities include:',
    'integration.cap1': 'Develop responsible investment policies and ESG investment guidelines aligned with institutional objectives',
    'integration.cap2': 'Integrate ESG evaluation toolkits into investment analysis and due diligence processes',
    'integration.cap3': 'Support development of ESG-themed investment products, including sustainability, climate, and impact funds',
    'integration.cap4': 'Enable active ESG engagement and proxy voting strategies for impact-oriented investment approaches',
    'integration.cap5': 'Conduct training programs on ESG integration for investment teams and analysts',
    'integration.cap6': 'Integrate ESG raw data and analytics into market data platforms and investment research tools',
    'integration.step1.title': 'Build Responsible Investment Frameworks',
    'integration.step1.desc': 'Develop policies and guidelines aligned with institutional objectives.',
    'integration.step2.title': 'Enhance Analysis and Due Diligence',
    'integration.step2.desc': 'Integrate ESG evaluation toolkits into the investment process.',
    'integration.step3.title': 'Design Targeted ESG Funds',
    'integration.step3.desc': 'Support development of sustainability, climate, and impact-themed products.',
    'integration.step4.title': 'Drive Impact Through Active Engagement',
    'integration.step4.desc': 'Enable ESG-focused proxy voting and engagement strategies.',
    'integration.step5.title': 'Integrate ESG Data and Analytics',
    'integration.step5.desc': 'Embed raw ESG data and research into market platforms.',

    // Contact Page
    'contact.badge': 'Get in Touch',
    'contact.title': 'Unlock ESG Intelligence for Middle East Markets',
    'contact.subtitle': 'Experience the full power of ESG Invest. Get access to ESG ratings for 880+ Middle East companies.',
    'contact.requestDemo': 'Request Access',
    'contact.formDesc': 'Fill out the form below and we\'ll set up your account within 24 hours.',
    'contact.form.firstName': 'First Name',
    'contact.form.lastName': 'Last Name',
    'contact.form.email': 'Work Email',
    'contact.form.company': 'Company/Organization',
    'contact.form.jobTitle': 'Job Title',
    'contact.form.employees': 'Investment Focus',
    'contact.form.selectSize': 'Select focus area',
    'contact.form.size1': 'Individual Investor',
    'contact.form.size2': 'Asset Management',
    'contact.form.size3': 'Institutional Investor',
    'contact.form.size4': 'Financial Advisor',
    'contact.form.message': 'Message (Optional)',
    'contact.form.messagePlaceholder': 'Tell us about your ESG investment interests...',
    'contact.form.submit': 'Request Access',
    'contact.info.title': 'Contact Information',
    'contact.info.email': 'Email',
    'contact.info.phone': 'Phone',
    'contact.info.address': 'Address',
    'contact.info.addressValue': 'ESG Invest HQ\nDubai, UAE',
    'contact.whatToExpect': 'What You Get',
    'contact.benefit1': 'Access to 880+ company ESG ratings',
    'contact.benefit2': 'Portfolio tracking and benchmarking tools',
    'contact.benefit3': 'Detailed ESG KPIs and metrics',
    'contact.benefit4': '14-day free trial with full access',
    'contact.benefit5': 'Dedicated onboarding support',
    'contact.responseTime': 'Response Time',
    'contact.within24': 'Within 24 hours',
    'contact.scheduleQuick': 'We\'ll set up your account quickly',
    'contact.getInTouch': 'Get in touch with our team',
    'contact.officeHours': 'Sun-Thu from 9am to 6pm GST',
    'contact.visitHQ': 'Visit our headquarters',
    'contact.toast.title': 'Request submitted!',
    'contact.toast.desc': 'We\'ll set up your account and reach out within 24 hours.',
    
    // Feature highlights
    'feature.highlight1.1': 'ESG ratings for 880+ listed companies',
    'feature.highlight1.2': 'Coverage across 10+ Middle East markets',
    'feature.highlight1.3': 'Independent ESG Invest® rating framework',
    'feature.highlight2.1': 'Compare ESG performance across sectors and markets',
    'feature.highlight2.2': 'Identify ESG leaders and laggards',
    'feature.highlight2.3': 'Conduct peer group benchmarking and analysis',
    'feature.highlight3.1': 'Build custom investment portfolios',
    'feature.highlight3.2': 'Track portfolio-level ESG ratings',
    'feature.highlight3.3': 'Monitor ESG performance and sustainability trends',
    'feature.highlight4.1': 'Comprehensive ESG company analysis',
    'feature.highlight4.2': 'Detailed recommendations and peer comparisons',
    'feature.highlight4.3': 'Exportable PDF reports for research and investment use',
    'feature.highlight5.1': '10+ years of historical data',
    'feature.highlight5.2': 'Trend analysis and charts',
    'feature.highlight5.3': 'Improvement tracking',
    'feature.highlight6.1': '150+ individual ESG metrics',
    'feature.highlight6.2': 'Environmental, Social, Governance breakdown',
    'feature.highlight6.3': 'Detailed scoring methodology',
    'feature.highlight7.1': 'Industry-specific benchmarks',
    'feature.highlight7.2': 'Sector ESG averages',
    'feature.highlight7.3': 'Best-in-class identification',
    'feature.highlight8.1': 'UAE, Saudi Arabia, Kuwait coverage',
    'feature.highlight8.2': 'Qatar, Bahrain, Oman markets',
    'feature.highlight8.3': 'Egypt, Jordan, Morocco included',
    'feature.highlight9.1': 'Rating change notifications',
    'feature.highlight9.2': 'Portfolio alerts',
    'feature.highlight9.3': 'News and updates',
    'feature.highlight10.1': 'Excel and CSV export options',
    'feature.highlight10.2': 'API access available',
    'feature.highlight10.3': 'Support for institutional data use',
    
    // Navigation
    'nav.subscriptions': 'Subscriptions',

    // Subscriptions Page
    'sub.badge': 'Flexible Plans',
    'sub.title': 'Subscription Plans',
    'sub.subtitle': 'Choose the ESG data coverage that fits your investment strategy. All plans provide access to ESG ratings and analytics for listed companies across Middle East markets.',
    'sub.popular': 'Most Popular',
    'sub.perMonth': 'month',
    'sub.perYear': 'year',
    'sub.discountNote': '10% discount applied on each additional market',
    'sub.plan.free.name': 'Free',
    'sub.plan.free.price': '$0',
    'sub.plan.free.desc': 'Access all features with limited data visibility',
    'sub.plan.free.cta': 'Get Started',
    'sub.plan.standard.name': 'Standard',
    'sub.plan.standard.price': '$99',
    'sub.plan.standard.desc': 'For serious ESG investors',
    'sub.plan.standard.cta': 'Contact Sales',
    'sub.plan.premium.name': 'Premium',
    'sub.plan.premium.price': '$199',
    'sub.plan.premium.desc': 'For institutions and asset managers',
    'sub.plan.premium.cta': 'Get Started',
    'sub.aumLabel': 'Assets Under Management (USD)',
    'sub.selectAum': 'Select AUM',
    'sub.aum.below100m': 'Below 100 million USD',
    'sub.aum.above100m': 'Above 100 million USD',
    'sub.selectMarket': 'Select Markets',
    'sub.marketsSelected': 'markets selected',
    'sub.market.all': 'All Markets',
    'sub.market.gcc': 'GCC Markets',
    'sub.market.saudi': 'Saudi Arabia',
    'sub.market.adx': 'ADX (Abu Dhabi)',
    'sub.market.dfm': 'DFM (Dubai)',
    'sub.market.kuwait': 'Kuwait',
    'sub.market.qatar': 'Qatar',
    'sub.market.bahrain': 'Bahrain',
    'sub.market.oman': 'Oman',
    'sub.market.jordan': 'Jordan',
    'sub.market.lebanon': 'Lebanon',
    'sub.market.palestine': 'Palestine',
    'sub.feature.allFeaturesLimited': 'Access to all features with limited data visibility',
    'sub.feature.limitedRatings': 'Limited ESG scores preview',
    'sub.feature.portfoliosLimited': 'Limited portfolio creation',
    'sub.feature.benchmarkingLimited': 'Limited company benchmarking',
    'sub.feature.reportsLimited': 'Limited analytical reports',
    'sub.feature.exportLimited': 'Limited data export',
    'sub.feature.kpisLimited': 'Limited ESG KPIs access',
    'sub.feature.companies50': 'Access to 50 company ratings',
    'sub.feature.companies300': 'Access to all purchased market(s) ratings',
    'sub.feature.companiesAll': 'Access to all purchased market(s) ratings',
    'sub.feature.basicRatings': 'Basic ESG scores',
    'sub.feature.fullRatings': 'Full ESG ratings & breakdown',
    'sub.feature.countryFilter': 'Country & sector filters',
    'sub.feature.portfolios': 'Portfolio creation',
    'sub.feature.portfolios3': 'Unlimited Portfolios',
    'sub.feature.portfoliosUnlimited': 'Unlimited portfolios',
    'sub.feature.benchmarking': 'Company benchmarking',
    'sub.feature.reports': 'Analytical reports',
    'sub.feature.reports5': 'Unlimited Reports',
    'sub.feature.reportsUnlimited': 'Unlimited reports',
    'sub.feature.kpis': '150+ ESG KPIs access',
    'sub.feature.export': 'Data export (Excel, CSV)',
    'sub.feature.api': 'API access',
    'sub.customNote': 'Need a custom plan for your organization? We can tailor a solution to your specific needs.',
    'sub.contactSales': 'Book Demo',

    // Trial Access card
    'sub.trial.heading': 'Trial Access',
    'sub.trial.desc': 'Explore ESG ratings, analytics, and platform capabilities',
    'sub.trial.limitedAccess': 'Limited Access',
    'sub.trial.viewSample': 'Preview ESG Report',
    'sub.trial.register': 'Request Trial Access',

    // Individual Reports card
    'sub.pro.heading': 'Professional Plan',
    'sub.pro.desc': 'For institutional investors and asset managers',
    'sub.pro.customPricing': 'Custom Pricing',
    'sub.pro.pricingNote': 'Tailored based on market coverage and AUM',
    'sub.pro.perYear': 'per year',
    'sub.pro.bundleDiscount': 'Bundle discount applied',
    'sub.pro.viewSample': 'Preview ESG Report',
    'sub.individual.heading': 'Individual Reports',
    'sub.individual.desc': 'For retail & individual investors',
    'sub.individual.selectCountry': 'Country',
    'sub.individual.selectSector': 'Sector',
    'sub.individual.selectCompany': 'Company',
    'sub.individual.allCountries': 'All Countries',
    'sub.individual.allSectors': 'All Sectors',
    'sub.individual.allCompanies': 'Select Companies',
    'sub.individual.feature1': 'Full ESG rating for one company',
    'sub.individual.feature2': 'Environmental, Social & Governance breakdown',
    'sub.individual.feature3': '150+ ESG KPIs covered',
    'sub.individual.feature4': 'Sector & peer comparison',
    'sub.individual.feature5': 'PDF report delivery',
    'sub.individual.feature6': 'One-time purchase — no subscription needed',
    'sub.individual.perReport': 'Per company report',
    'sub.individual.reports': 'reports',
    'sub.individual.viewSample': 'Preview ESG Report',
    'sub.individual.requestReport': 'Get Report',

    // Feature section pills
    'pill.ratings': 'Ratings',
    'pill.benchmarking': 'Benchmarking',
    'pill.portfolio': 'Portfolio',
    'pill.reports': 'Reports',
    'pill.analytics': 'Analytics',
    'pill.dataExport': 'Data Export',
    'pill.integration': 'Integration',

    // Top 5 ESG Section
    'top5.title': 'Top ESG-Rated Companies in the Middle East',
    'top5.overallScore': 'Overall ESG Score',
    'top5.environmental': 'Environmental',
    'top5.social': 'Social',
    'top5.governance': 'Governance',

    // Footer
    // Companies Page
    'companies.badge': '880+ Listed Companies',
    'companies.explore': 'Explore companies by market',
    'companies.title': 'Companies Covered',
    'companies.subtitle': 'Explore ESG ratings, sustainability insights, and performance benchmarks for 880+ listed companies across Middle East markets.',
    'companies.searchPlaceholder': 'Search by company name...',
    'companies.allCountries': 'All Countries',
    'companies.allSectors': 'All Sectors',
    'companies.showing': 'Showing',
    'companies.of': 'of',
    'companies.companies': 'companies',
    'companies.noResults': 'No companies found matching your search criteria.',

    'footer.description': 'ESG Invest provides comprehensive ESG ratings and analytics for 880+ listed companies across the Middle East, helping investors make sustainable investment decisions.',
    'footer.product': 'Insights',
    'footer.documentation': 'ESG Market Insights',
    'footer.company': 'About ESG Invest',
    'footer.resources': 'Resources',
    'footer.about': 'About',
    'footer.privacy': 'Privacy',
    'footer.methodology': 'ESG Rating Methodology',
    'footer.codeOfConduct': 'Code of Conduct',
    'footer.copyright': '© 2026 ESG Invest. All rights reserved.',
    'footer.cookieSettings': 'Cookie Settings',

    // Lead form modal (shared)
    'lead.form.name': 'Full Name',
    'lead.form.email': 'Work Email',
    'lead.form.company': 'Company',
    'lead.form.phone': 'Phone',
    'lead.form.message': 'Message',
    'lead.form.messagePlaceholder': 'Tell us a bit about what you need...',
    'lead.form.submit': 'Send Request',
    'lead.form.submitting': 'Sending...',
    'lead.form.captcha': 'Quick check',
    'lead.form.captchaError': 'Could not load — refresh',
    'lead.form.captchaReload': 'Reload challenge',
    'lead.form.captchaWrong': 'Incorrect answer to the quick check. Please try again.',
    'lead.form.workEmailRequired': "Please use your work email. Free providers like Gmail or Yahoo aren't accepted.",
    'lead.toast.successTitle': 'Request received',
    'lead.toast.successDesc': 'Thanks — our team will reach out within one business day.',
    'lead.toast.errorDesc': 'Something went wrong. Please try again.',
    'lead.notSpecified': 'Not specified',
    'lead.noneSelected': 'None selected',
    'lead.estimatedFee': 'Estimated fee',
    'lead.estimatedTotal': 'Estimated total',
    'lead.pro.title': 'Book a demo of the Professional plan',
    'lead.pro.desc': 'Share your details and our team will set up a tailored walkthrough for your selections below.',
    'booking.step.details': 'Your details',
    'booking.step.time': 'Pick a time',
    'booking.form.sector': 'Industry / Sector',
    'booking.form.sectorPlaceholder': 'Select your industry (optional)',
    'booking.continue': 'Continue',
    'booking.back': 'Back',
    'booking.loadingSlots': 'Loading available times…',
    'booking.noSlots': 'No times available right now. Please try again later or contact us.',
    'booking.timezoneNote': 'Times shown in your timezone ({tz}).',
    'booking.selectSlotFirst': 'Please select a time slot.',
    'booking.slotTaken': 'That time was just taken. Please pick another.',
    'booking.availabilityError': 'Could not load availability. Please try again.',
    'booking.confirm': 'Confirm booking',
    'booking.confirming': 'Confirming…',
    'booking.success.title': 'Your demo is booked',
    'booking.success.desc': 'A calendar invitation is on its way to {email}. We look forward to speaking with you.',
    'booking.success.close': 'Done',
    'lead.individual.title': 'Purchase ESG report(s)',
    'lead.individual.desc': 'Confirm your selections and our team will follow up with payment instructions and delivery details.',

    // Cookie consent
    'cookie.title': 'We value your privacy',
    'cookie.description': 'We use cookies to operate our site, measure performance, and improve your experience. You can accept all cookies, reject non-essential ones, or choose which categories to allow. You can change your choice anytime from "Cookie Settings" in the footer.',
    'cookie.necessary': 'Strictly necessary',
    'cookie.necessaryDesc': 'Required for the site to function (security, preferences, language). Cannot be disabled.',
    'cookie.analytics': 'Analytics',
    'cookie.analyticsDesc': 'Help us understand how visitors use the site so we can improve it.',
    'cookie.marketing': 'Marketing',
    'cookie.marketingDesc': 'Used to measure and personalize advertising and outreach campaigns.',
    'cookie.acceptAll': 'Accept all',
    'cookie.rejectAll': 'Reject non-essential',
    'cookie.customize': 'Customize',
    'cookie.savePreferences': 'Save preferences',
  },
  ar: {
    // Navigation
    'nav.home': 'الرئيسية',
    'nav.features': 'المميزات',
    'nav.contact': 'اتصل بنا',
    'nav.companies': 'الشركات',
    'nav.signin': 'تسجيل الدخول',
    'nav.bookDemo': 'ابدأ الآن',
    
    // Hero Section
    'hero.badge': 'الرائد في تصنيفات ESG بالشرق الأوسط',
    'hero.title1': 'ذكاء ESG',
    'hero.title2': 'للمستثمرين',
    'hero.description': 'احصل على تصنيفات ESG الشاملة لأكثر من 800 شركة مدرجة في الشرق الأوسط. أنشئ محافظ استثمارية وتتبع الأداء واتخذ قرارات استثمارية مستنيرة.',
    'hero.startTrial': 'تسجيل الدخول',
    'hero.tryFree': 'جرب مجانًا',
    'hero.watchDemo': 'شاهد العرض',
    'hero.esgScore': 'متوسط درجة ESG',
    'hero.excellent': 'ممتاز',
    'hero.environmental': 'بيئي',
    'hero.social': 'اجتماعي',
    'hero.governance': 'حوكمة',
    
    // Stats
    'stats.companies': '+800',
    'stats.companiesLabel': 'شركة مدرجة',
    'topFive.rankings': 'تصنيفات ESG',
    'topFive.performer': 'أفضل أداء في ESG',
    'topFive.scoreOutOf': 'الدرجة من 100',
    'stats.countries': '+10',
    'stats.countriesLabel': 'سوق في الشرق الأوسط',
    'stats.kpis': '+150',
    'stats.kpisLabel': 'مؤشر ESG',
    'stats.years': '+10',
    'stats.yearsLabel': 'سنوات من البيانات',
    
    // Features Section
    'features.title': 'تصنيفات ESG والبيانات والرؤى للمستثمرين',
    'features.subtitle': 'أدوات وبيانات شاملة مصممة لمساعدة المستثمرين على تقييم أداء الاستدامة في منطقة الشرق الأوسط.',
    'features.companyRatings.title': 'تصنيفات ESG للشركات',
    'features.companyRatings.desc': 'احصل على تصنيفات ESG المفصلة لأكثر من 800 شركة مدرجة في بورصات الشرق الأوسط.',
    'features.portfolioBuilder.title': 'منشئ المحافظ',
    'features.portfolioBuilder.desc': 'أنشئ وأدر محافظ استثمارية مع تتبع أداء ESG الإجمالي بمرور الوقت.',
    'features.benchmarking.title': 'مقارنة الشركات',
    'features.benchmarking.desc': 'قارن أداء ESG عبر الشركات والقطاعات والأسواق لتحديد القادة والمتأخرين.',
    'features.kpiAccess.title': 'مؤشرات ESG التفصيلية',
    'features.kpiAccess.desc': 'تعمق في أكثر من 150 مقياسًا فرديًا يغطي التأثير البيئي والمسؤولية الاجتماعية وممارسات الحوكمة.',
    'features.analytics.title': 'التحليلات التاريخية',
    'features.analytics.desc': 'تتبع اتجاهات تصنيفات ESG على مدار سنوات متعددة لفهم مسارات الشركات.',
    'features.reports.title': 'التقارير المتميزة',
    'features.reports.desc': 'اشترِ تقارير تحليلية شاملة مع رؤى مفصلة وتوصيات ومقارنات.',
    
    // Benefits Section
    'benefits.title': 'اتخذ قرارات استثمارية مستدامة',
    'benefits.subtitle': 'استفد من تصنيفات ESG ومؤشرات الاستدامة والأدوات التحليلية لدعم استراتيجيات الاستثمار المسؤول في أسواق رأس المال بالشرق الأوسط.',
    'benefits.item1': 'تصنيفات لأكثر من 800 شركة في الشرق الأوسط',
    'benefits.item2': 'تتبع أداء ESG للمحفظة بمرور الوقت',
    'benefits.item3': 'قارن الشركات مع نظيراتها في الصناعة',
    'benefits.item4': 'حلل أكثر من 150 مؤشر ESG مفصل',
    'benefits.item5': 'حمّل تقارير تحليلية متميزة',
    'benefits.coverage': 'تغطية إقليمية',
    'benefits.coverageDesc': 'تغطية بورصات الشرق الأوسط الرئيسية، مع رؤى ESG للشركات المدرجة في جميع أنحاء المنطقة.',
    'benefits.methodology': 'منهجية موثوقة',
    'benefits.methodologyDesc': 'تستند تصنيفات ESG إلى إطار التصنيف الخاص بـ ESG Invest®، المطوَّر وفق معايير ESG الدولية المعترف بها والخبرة الإقليمية.',
    'benefits.updates': 'تحديثات منتظمة',
    'benefits.updatesDesc': 'تُحدَّث تصنيفات ESG والبيانات الأساسية بانتظام لتعكس أحدث الإفصاحات المؤسسية وتطورات الأداء في مجال الاستدامة.',
    'benefits.support': 'دعم الخبراء',
    'benefits.supportDesc': 'يقدم محللو ESG المتخصصون التوجيه والدعم للمستثمرين الساعين إلى دمج اعتبارات ESG في التحليل الاستثماري وبناء المحافظ.',
    
    // CTA Section
    'cta.title': 'ابدأ استكشاف رؤى ESG في أسواق الشرق الأوسط',
    'cta.subtitle': 'احصل على تصنيفات ESG وأدوات المقارنة ورؤى الاستدامة لأكثر من 880 شركة مدرجة في أسواق الشرق الأوسط.',
    'cta.scheduleDemo': 'ابدأ تجربة مجانية',
    'cta.startForFree': 'استكشف المنصة',
    
    // Features Page
    'featuresPage.badge': 'إمكانيات المنصة',
    'featuresPage.title': 'أدوات استثمار ESG قوية',
    'featuresPage.subtitle': 'اكتشف كيف يساعدك ESG Invest في تقييم وتتبع الاستثمارات المستدامة.',
    'featuresPage.keyFeatures': 'المميزات الرئيسية',
    'featuresPage.feature1.title': 'تصنيفات ESG للشركات',
    'featuresPage.feature1.desc': 'احصل على تصنيفات ESG الشاملة لأكثر من 880 شركة مدرجة في أكثر من 10 أسواق في الشرق الأوسط، استناداً إلى منهجية ESG Invest® الخاصة.',
    'featuresPage.feature2.title': 'مقارنة الشركات',
    'featuresPage.feature2.desc': 'قارن أداء ESG عبر الشركات والقطاعات والدول لتحديد فرص الاستثمار.',
    'featuresPage.feature3.title': 'إدارة المحافظ',
    'featuresPage.feature3.desc': 'أنشئ محافظ استثمارية وتتبع تصنيفات ESG الإجمالية والأداء بمرور الوقت.',
    'featuresPage.feature4.title': 'تقارير تحليلية متميزة',
    'featuresPage.feature4.desc': 'اشترِ تقارير مفصلة مع تحليل عميق ومقارنات وتوصيات.',
    'featuresPage.feature5.title': 'تتبع الاتجاهات',
    'featuresPage.feature5.desc': 'حلل اتجاهات تصنيفات ESG على مدار سنوات متعددة.',
    'featuresPage.feature6.title': 'مؤشرات ESG التفصيلية',
    'featuresPage.feature6.desc': 'احصل على أكثر من 150 مقياسًا فرديًا عبر الفئات البيئية والاجتماعية والحوكمة.',
    'featuresPage.feature7.title': 'تحليل القطاعات',
    'featuresPage.feature7.desc': 'قيّم أداء ESG حسب قطاع الصناعة للعثور على القادة المستدامين.',
    'featuresPage.feature8.title': 'تغطية الدول',
    'featuresPage.feature8.desc': 'تغطية كاملة للبورصات الرئيسية بما في ذلك الإمارات والسعودية والكويت.',
    'featuresPage.feature9.title': 'إشعارات التنبيه',
    'featuresPage.feature9.desc': 'احصل على إشعارات عند تغيير تصنيفات ESG للشركات في محفظتك.',
    'featuresPage.feature10.title': 'تصدير البيانات',
    'featuresPage.feature10.desc': 'صدّر بيانات وتصنيفات ESG للتكامل مع أدوات التحليل الخاصة بك.',

    // ESG Integration Tools Section
    'integration.title': 'أدوات تكامل ESG',
    'integration.desc1': 'دعم تكامل الاعتبارات البيئية والاجتماعية والحوكمة في استراتيجيات الاستثمار والمنتجات المالية.',
    'integration.desc2': 'توفر المنصة توجيهات وأدوات عملية لمساعدة المستثمرين والمشاركين في السوق على دمج عوامل ESG في عمليات الاستثمار وصنع القرار.',
    'integration.capabilities': 'تشمل القدرات:',
    'integration.cap1': 'تطوير سياسات الاستثمار المسؤول وإرشادات استثمار ESG المتوافقة مع الأهداف المؤسسية',
    'integration.cap2': 'دمج أدوات تقييم ESG في تحليل الاستثمار وعمليات العناية الواجبة',
    'integration.cap3': 'دعم تطوير منتجات استثمارية ذات طابع ESG، بما في ذلك صناديق الاستدامة والمناخ والأثر',
    'integration.cap4': 'تمكين استراتيجيات مشاركة ESG الفعّالة والتصويت بالوكالة للمناهج الاستثمارية الموجهة نحو الأثر',
    'integration.cap5': 'إجراء برامج تدريبية حول تكامل ESG لفرق الاستثمار والمحللين',
    'integration.cap6': 'دمج بيانات ESG الخام والتحليلات في منصات بيانات السوق وأدوات البحث الاستثماري',
    'integration.step1.title': 'بناء أطر الاستثمار المسؤول',
    'integration.step1.desc': 'تطوير السياسات والإرشادات المتوافقة مع الأهداف المؤسسية.',
    'integration.step2.title': 'تعزيز التحليل والعناية الواجبة',
    'integration.step2.desc': 'دمج أدوات تقييم ESG في عملية الاستثمار.',
    'integration.step3.title': 'تصميم صناديق ESG المستهدفة',
    'integration.step3.desc': 'دعم تطوير منتجات الاستدامة والمناخ وذات الأثر.',
    'integration.step4.title': 'تحقيق الأثر من خلال المشاركة الفعّالة',
    'integration.step4.desc': 'تمكين التصويت بالوكالة واستراتيجيات المشاركة الموجهة نحو ESG.',
    'integration.step5.title': 'دمج بيانات وتحليلات ESG',
    'integration.step5.desc': 'دمج بيانات وأبحاث ESG الخام في منصات السوق.',

    // Contact Page
    'contact.badge': 'تواصل معنا',
    'contact.title': 'اطلق قدرات ESG Intelligence لأسواق الشرق الأوسط',
    'contact.subtitle': 'اختبر القوة الكاملة لـ ESG Invest. احصل على تصنيفات ESG لأكثر من 880 شركة مدرجة في الشرق الأوسط.',
    'contact.requestDemo': 'طلب الوصول',
    'contact.formDesc': 'املأ النموذج أدناه وسننشئ حسابك خلال 24 ساعة.',
    'contact.form.firstName': 'الاسم الأول',
    'contact.form.lastName': 'اسم العائلة',
    'contact.form.email': 'البريد الإلكتروني للعمل',
    'contact.form.company': 'الشركة/المؤسسة',
    'contact.form.jobTitle': 'المسمى الوظيفي',
    'contact.form.employees': 'تركيز الاستثمار',
    'contact.form.selectSize': 'اختر مجال التركيز',
    'contact.form.size1': 'مستثمر فردي',
    'contact.form.size2': 'إدارة الأصول',
    'contact.form.size3': 'مستثمر مؤسسي',
    'contact.form.size4': 'مستشار مالي',
    'contact.form.message': 'الرسالة (اختياري)',
    'contact.form.messagePlaceholder': 'أخبرنا عن اهتماماتك في الاستثمار المستدام...',
    'contact.form.submit': 'إرسال رسالة',
    'contact.info.title': 'معلومات الاتصال',
    'contact.info.email': 'البريد الإلكتروني',
    'contact.info.phone': 'الهاتف',
    'contact.info.address': 'العنوان',
    'contact.info.addressValue': 'المقر الرئيسي لـ ESG Invest\nدبي، الإمارات',
    'contact.whatToExpect': 'ما ستحصل عليه',
    'contact.benefit1': 'الوصول إلى تصنيفات 880+ شركة',
    'contact.benefit2': 'أدوات تتبع المحافظ والمقارنة',
    'contact.benefit3': 'مؤشرات ESG ومقاييس مفصلة',
    'contact.benefit4': 'تجربة مجانية لمدة 14 يومًا',
    'contact.benefit5': 'دعم تأهيل مخصص',
    'contact.responseTime': 'وقت الاستجابة',
    'contact.within24': 'خلال 24 ساعة',
    'contact.scheduleQuick': 'سننشئ حسابك بسرعة',
    'contact.getInTouch': 'تواصل مع فريقنا',
    'contact.officeHours': 'الأحد-الخميس من 9 صباحًا إلى 6 مساءً',
    'contact.visitHQ': 'قم بزيارة مقرنا الرئيسي',
    'contact.toast.title': 'تم تقديم الطلب!',
    'contact.toast.desc': 'سننشئ حسابك ونتواصل معك خلال 24 ساعة.',
    
    // Feature highlights
    'feature.highlight1.1': 'تصنيفات ESG لأكثر من 800 شركة مدرجة',
    'feature.highlight1.2': 'تغطية أكثر من 10 أسواق في الشرق الأوسط',
    'feature.highlight1.3': 'إطار تصنيف ESG Invest® المستقل',
    'feature.highlight2.1': 'قارن عبر القطاعات والأسواق',
    'feature.highlight2.2': 'حدد قادة ومتأخري ESG',
    'feature.highlight2.3': 'تحليل مجموعة الأقران',
    'feature.highlight3.1': 'أنشئ محافظ استثمارية مخصصة',
    'feature.highlight3.2': 'تتبع أداء ESG الإجمالي',
    'feature.highlight3.3': 'راقب التغييرات بمرور الوقت',
    'feature.highlight4.1': 'تحليل شامل للشركات',
    'feature.highlight4.2': 'توصيات مفصلة',
    'feature.highlight4.3': 'تقارير PDF قابلة للتصدير',
    'feature.highlight5.1': 'أكثر من 10 سنوات من البيانات التاريخية',
    'feature.highlight5.2': 'تحليل الاتجاهات والرسوم البيانية',
    'feature.highlight5.3': 'تتبع التحسن',
    'feature.highlight6.1': 'أكثر من 150 مقياس ESG فردي',
    'feature.highlight6.2': 'تفصيل بيئي واجتماعي وحوكمة',
    'feature.highlight6.3': 'منهجية تسجيل مفصلة',
    'feature.highlight7.1': 'معايير خاصة بالصناعة',
    'feature.highlight7.2': 'متوسطات ESG للقطاع',
    'feature.highlight7.3': 'تحديد الأفضل في فئته',
    'feature.highlight8.1': 'تغطية الإمارات والسعودية والكويت',
    'feature.highlight8.2': 'أسواق قطر والبحرين وعمان',
    'feature.highlight8.3': 'مصر والأردن والمغرب مشمولة',
    'feature.highlight9.1': 'إشعارات تغيير التصنيف',
    'feature.highlight9.2': 'تنبيهات المحفظة',
    'feature.highlight9.3': 'الأخبار والتحديثات',
    'feature.highlight10.1': 'تصدير Excel و CSV',
    'feature.highlight10.2': 'وصول API متاح',
    'feature.highlight10.3': 'دعم التكامل',
    
    // Navigation
    'nav.subscriptions': 'الاشتراكات',

    // Subscriptions Page
    'sub.badge': 'خطط مرنة',
    'sub.title': 'خطط الاشتراك',
    'sub.subtitle': 'اختر الخطة التي تناسب احتياجاتك في الاستثمار المستدام. جميع الخطط تتضمن الوصول إلى قاعدة بيانات ESG للشرق الأوسط.',
    'sub.popular': 'الأكثر شعبية',
    'sub.perMonth': 'شهر',
    'sub.perYear': 'سنة',
    'sub.discountNote': 'خصم 10% على كل سوق إضافي',
    'sub.plan.free.name': 'مجاني',
    'sub.plan.free.price': '$0',
    'sub.plan.free.desc': 'الوصول إلى جميع الميزات مع رؤية محدودة للبيانات',
    'sub.plan.free.cta': 'ابدأ الآن',
    'sub.plan.standard.name': 'الأساسي',
    'sub.plan.standard.price': '$99',
    'sub.plan.standard.desc': 'للمستثمرين الجادين في ESG',
    'sub.plan.standard.cta': 'تواصل مع المبيعات',
    'sub.plan.premium.name': 'المميز',
    'sub.plan.premium.price': '$199',
    'sub.plan.premium.desc': 'للمؤسسات ومديري الأصول',
    'sub.plan.premium.cta': 'ابدأ الآن',
    'sub.aumLabel': 'الأصول تحت الإدارة (بالدولار الأمريكي)',
    'sub.selectAum': 'اختر نطاق الأصول',
    'sub.aum.below100m': 'أقل من 100 مليون دولار',
    'sub.aum.above100m': 'أكثر من 100 مليون دولار',
    'sub.selectMarket': 'اختر الأسواق',
    'sub.marketsSelected': 'أسواق مختارة',
    'sub.market.all': 'جميع الأسواق',
    'sub.market.gcc': 'أسواق الخليج',
    'sub.market.saudi': 'السعودية',
    'sub.market.adx': 'سوق أبوظبي',
    'sub.market.dfm': 'سوق دبي',
    'sub.market.kuwait': 'الكويت',
    'sub.market.qatar': 'قطر',
    'sub.market.bahrain': 'البحرين',
    'sub.market.oman': 'عمان',
    'sub.market.jordan': 'الأردن',
    'sub.market.lebanon': 'لبنان',
    'sub.market.palestine': 'فلسطين',
    'sub.feature.allFeaturesLimited': 'الوصول إلى جميع الميزات مع رؤية محدودة للبيانات',
    'sub.feature.limitedRatings': 'معاينة محدودة لدرجات ESG',
    'sub.feature.portfoliosLimited': 'إنشاء محافظ محدود',
    'sub.feature.benchmarkingLimited': 'مقارنة شركات محدودة',
    'sub.feature.reportsLimited': 'تقارير تحليلية محدودة',
    'sub.feature.exportLimited': 'تصدير بيانات محدود',
    'sub.feature.kpisLimited': 'وصول محدود لمؤشرات ESG',
    'sub.feature.companies50': 'الوصول إلى تصنيفات 50 شركة',
    'sub.feature.companies300': 'الوصول إلى جميع تصنيفات الأسواق المشتراة',
    'sub.feature.companiesAll': 'الوصول إلى جميع تصنيفات الأسواق المشتراة',
    'sub.feature.basicRatings': 'درجات ESG الأساسية',
    'sub.feature.fullRatings': 'تصنيفات ESG كاملة مع التفصيل',
    'sub.feature.countryFilter': 'فلاتر الدولة والقطاع',
    'sub.feature.portfolios': 'إنشاء المحافظ',
    'sub.feature.portfolios3': 'محافظ غير محدودة',
    'sub.feature.portfoliosUnlimited': 'محافظ غير محدودة',
    'sub.feature.benchmarking': 'مقارنة الشركات',
    'sub.feature.reports': 'تقارير تحليلية',
    'sub.feature.reports5': 'تقارير غير محدودة',
    'sub.feature.reportsUnlimited': 'تقارير غير محدودة',
    'sub.feature.kpis': 'الوصول إلى 150+ مؤشر ESG',
    'sub.feature.export': 'تصدير البيانات (Excel, CSV)',
    'sub.feature.api': 'وصول API',
    'sub.customNote': 'هل تحتاج خطة مخصصة لمؤسستك؟ يمكننا تصميم حل يناسب احتياجاتك.',
    'sub.contactSales': 'احجز عرضاً تجريبياً',

    // Trial Access card
    'sub.trial.heading': 'تجربة مجانية',
    'sub.trial.desc': 'استكشف تصنيفات ESG والتحليلات وإمكانيات المنصة',
    'sub.trial.limitedAccess': 'وصول محدود',
    'sub.trial.viewSample': 'معاينة تقرير ESG',
    'sub.trial.register': 'طلب تجربة مجانية',

    // Individual Reports card
    'sub.pro.heading': 'الخطة الاحترافية',
    'sub.pro.desc': 'للمستثمرين المؤسسيين ومديري الأصول',
    'sub.pro.customPricing': 'تسعير مخصص',
    'sub.pro.pricingNote': 'مصمم بناءً على تغطية السوق وحجم الأصول المدارة',
    'sub.pro.perYear': 'سنويًا',
    'sub.pro.bundleDiscount': 'تم تطبيق خصم الحزمة',
    'sub.pro.viewSample': 'معاينة تقرير ESG',
    'sub.individual.heading': 'التقارير الفردية',
    'sub.individual.desc': 'للمستثمرين الأفراد والتجزئة',
    'sub.individual.selectCountry': 'الدولة',
    'sub.individual.selectSector': 'القطاع',
    'sub.individual.selectCompany': 'الشركة',
    'sub.individual.allCountries': 'جميع الدول',
    'sub.individual.allSectors': 'جميع القطاعات',
    'sub.individual.allCompanies': 'اختر الشركات',
    'sub.individual.feature1': 'تصنيف ESG كامل لشركة واحدة',
    'sub.individual.feature2': 'تفصيل بيئي واجتماعي وحوكمة',
    'sub.individual.feature3': 'يشمل أكثر من 150 مؤشر ESG',
    'sub.individual.feature4': 'مقارنة القطاع والأقران',
    'sub.individual.feature5': 'تسليم تقرير PDF',
    'sub.individual.feature6': 'شراء لمرة واحدة — لا يلزم اشتراك',
    'sub.individual.perReport': 'لكل تقرير شركة',
    'sub.individual.reports': 'تقارير',
    'sub.individual.viewSample': 'معاينة تقرير ESG',
    'sub.individual.requestReport': 'احصل على تقرير',

    // Feature section pills
    'pill.ratings': 'التصنيفات',
    'pill.benchmarking': 'المقارنة المعيارية',
    'pill.portfolio': 'المحفظة',
    'pill.reports': 'التقارير',
    'pill.analytics': 'التحليلات',
    'pill.dataExport': 'تصدير البيانات',
    'pill.integration': 'التكامل',

    // Top 5 ESG Section
    'top5.title': 'أفضل خمس شركات في أداء ESG في الشرق الأوسط',
    'top5.overallScore': 'مجموع درجة ESG',
    'top5.environmental': 'البيئة',
    'top5.social': 'المجتمع',
    'top5.governance': 'الحوكمة',

    // Footer
    // Companies Page
    'companies.badge': '880+ شركة مدرجة',
    'companies.explore': 'استكشف الشركات حسب السوق',
    'companies.title': 'الشركات',
    'companies.subtitle': 'استكشف تصنيفات ESG لأكثر من 880 شركة مدرجة في الشرق الأوسط.',
    'companies.searchPlaceholder': 'ابحث باسم الشركة...',
    'companies.allCountries': 'جميع الدول',
    'companies.allSectors': 'جميع القطاعات',
    'companies.showing': 'عرض',
    'companies.of': 'من',
    'companies.companies': 'شركة',
    'companies.noResults': 'لم يتم العثور على شركات تطابق معايير البحث.',

    'footer.description': 'يوفر ESG Invest تصنيفات وتحليلات ESG شاملة لأكثر من 800 شركة مدرجة في الشرق الأوسط، مما يساعد المستثمرين على اتخاذ قرارات استثمارية مستدامة.',
    'footer.product': 'المنتج',
    'footer.documentation': 'رؤى سوق ESG',
    'footer.company': 'عن ESG Invest',
    'footer.resources': 'موارد',
    'footer.about': 'عن الشركة',
    'footer.privacy': 'الخصوصية',
    'footer.methodology': 'منهجية تصنيف ESG',
    'footer.codeOfConduct': 'مدونة السلوك',
    'footer.copyright': '© 2026 ESG Invest. جميع الحقوق محفوظة.',
    'footer.cookieSettings': 'إعدادات ملفات تعريف الارتباط',

    // Lead form modal (shared)
    'lead.form.name': 'الاسم الكامل',
    'lead.form.email': 'البريد الإلكتروني للعمل',
    'lead.form.company': 'الشركة',
    'lead.form.phone': 'رقم الهاتف',
    'lead.form.message': 'الرسالة',
    'lead.form.messagePlaceholder': 'أخبرنا قليلاً عمّا تحتاجه...',
    'lead.form.submit': 'إرسال الطلب',
    'lead.form.submitting': 'جارٍ الإرسال...',
    'lead.form.captcha': 'تحقّق سريع',
    'lead.form.captchaError': 'تعذّر التحميل — حدّث',
    'lead.form.captchaReload': 'إعادة تحميل التحقق',
    'lead.form.captchaWrong': 'الإجابة غير صحيحة على التحقق السريع. يرجى المحاولة مرة أخرى.',
    'lead.form.workEmailRequired': 'يرجى استخدام بريدك الإلكتروني الخاص بالعمل. لا نقبل مزودي البريد الإلكتروني المجاني مثل Gmail أو Yahoo.',
    'lead.toast.successTitle': 'تم استلام طلبك',
    'lead.toast.successDesc': 'شكرًا لك — سيتواصل معك فريقنا خلال يوم عمل واحد.',
    'lead.toast.errorDesc': 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    'lead.notSpecified': 'غير محدد',
    'lead.noneSelected': 'لم يتم تحديد شيء',
    'lead.estimatedFee': 'الرسوم التقديرية',
    'lead.estimatedTotal': 'المجموع التقديري',
    'lead.pro.title': 'احجز عرضًا للخطة الاحترافية',
    'lead.pro.desc': 'شارك بياناتك وسيقوم فريقنا بإعداد جولة مخصصة بناءً على اختياراتك أدناه.',
    'booking.step.details': 'بياناتك',
    'booking.step.time': 'اختر الوقت',
    'booking.form.sector': 'القطاع / الصناعة',
    'booking.form.sectorPlaceholder': 'اختر قطاعك (اختياري)',
    'booking.continue': 'متابعة',
    'booking.back': 'رجوع',
    'booking.loadingSlots': 'جارٍ تحميل الأوقات المتاحة…',
    'booking.noSlots': 'لا توجد أوقات متاحة حاليًا. يرجى المحاولة لاحقًا أو التواصل معنا.',
    'booking.timezoneNote': 'الأوقات معروضة بتوقيتك المحلي ({tz}).',
    'booking.selectSlotFirst': 'يرجى اختيار موعد.',
    'booking.slotTaken': 'تم حجز هذا الوقت للتو. يرجى اختيار وقت آخر.',
    'booking.availabilityError': 'تعذّر تحميل الأوقات المتاحة. يرجى المحاولة مرة أخرى.',
    'booking.confirm': 'تأكيد الحجز',
    'booking.confirming': 'جارٍ التأكيد…',
    'booking.success.title': 'تم حجز عرضك التوضيحي',
    'booking.success.desc': 'دعوة التقويم في طريقها إلى {email}. نتطلع للتحدث معك.',
    'booking.success.close': 'تم',
    'lead.individual.title': 'شراء تقارير ESG',
    'lead.individual.desc': 'أكّد اختياراتك وسيتواصل معك فريقنا بتعليمات الدفع وتفاصيل التسليم.',

    // Cookie consent
    'cookie.title': 'نحن نهتم بخصوصيتك',
    'cookie.description': 'نستخدم ملفات تعريف الارتباط لتشغيل موقعنا وقياس الأداء وتحسين تجربتك. يمكنك قبول جميع ملفات تعريف الارتباط، أو رفض غير الضرورية منها، أو اختيار الفئات التي تسمح بها. يمكنك تغيير اختيارك في أي وقت من "إعدادات ملفات تعريف الارتباط" في تذييل الصفحة.',
    'cookie.necessary': 'ضرورية تمامًا',
    'cookie.necessaryDesc': 'مطلوبة لعمل الموقع (الأمان والتفضيلات واللغة). لا يمكن تعطيلها.',
    'cookie.analytics': 'التحليلات',
    'cookie.analyticsDesc': 'تساعدنا على فهم كيفية استخدام الزوار للموقع لتحسينه.',
    'cookie.marketing': 'التسويق',
    'cookie.marketingDesc': 'تُستخدم لقياس وتخصيص الإعلانات وحملات التواصل.',
    'cookie.acceptAll': 'قبول الكل',
    'cookie.rejectAll': 'رفض غير الضرورية',
    'cookie.customize': 'تخصيص',
    'cookie.savePreferences': 'حفظ التفضيلات',
  }
};

const TranslationsContext = createContext<TranslationsContextType | undefined>(undefined);

export const TranslationsProvider = ({
  children,
  initialLanguage = 'en',
}: {
  children: ReactNode;
  initialLanguage?: Language;
}) => {
  const [language, setLanguage] = useState<Language>(initialLanguage);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const isRTL = language === 'ar';

  return (
    <TranslationsContext.Provider value={{ language, setLanguage, t, isRTL }}>
      {children}
    </TranslationsContext.Provider>
  );
};

export const useTranslations = () => {
  const context = useContext(TranslationsContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationsProvider');
  }
  return context;
};
